import CloseIcon from '@mui/icons-material/Close';
import { Box, LinearProgress, Portal, Slide, Snackbar, Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { snackbarAtom } from '@/atoms/snackbar';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';

export function Snackbars() {
    const snackbar = useAtomValue(snackbarAtom);
    const {
        open,
        title = '',
        message,
        severity = snackbarColor.info,
        vertical = 'bottom',
        horizontal = 'center',
        action,
        progress,
    } = snackbar;
    const showSnackbar = useSnackbar();

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        showSnackbar({
            title: '',
            message: '',
            open: false,
            progress: undefined,
        });
    };

    const handleActionClick = () => {
        action?.onClick();
        handleClose();
    };

    return (
        <Portal>
            <Stack spacing={2} sx={{ width: '100%' }}>
                <Snackbar
                    open={open}
                    autoHideDuration={progress != null ? null : 5000}
                    onClose={handleClose}
                    disableWindowBlurListener
                    TransitionComponent={Slide}
                    anchorOrigin={{ vertical, horizontal }}
                    sx={{
                        '&.MuiSnackbar-root': {
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 720 },
                            width: 'auto',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            borderRadius: '16px',
                            background: severity,
                        }}
                    >
                        <div className="w-[93%]">
                            <Text variant="body1" color="text-white" children={title} />
                            <Typography
                                color={
                                    severity === snackbarColor.error ||
                                    severity === snackbarColor.warning ||
                                    severity === snackbarColor.success
                                        ? 'primary'
                                        : 'secondary'
                                }
                                children={progress != null ? `${message} — ${Math.round(progress)}%` : message}
                            />
                            {progress != null && (
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.max(0, Math.min(100, progress))}
                                    sx={{ mt: 1, borderRadius: 1 }}
                                />
                            )}
                        </div>

                        {action && (
                            <Button
                                onClick={handleActionClick}
                                variant="text"
                                size="small"
                                className="normal-case text-c1 font-semibold mr-2 min-w-[auto]"
                            >
                                {action.label}
                            </Button>
                        )}
                        <CloseIcon
                            onClick={handleClose}
                            fontSize="small"
                            color="primary"
                            sx={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                verticalAlign: 'top',
                            }}
                        />
                    </Box>
                </Snackbar>
            </Stack>
        </Portal>
    );
}
